import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { getUniqueId, reorder } from "@jugl-web/utils";
import { FC, useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { TaskFieldSidebarListItemLayout } from "../../../TaskFieldSidebarListItemLayout";
import { AddNewValueButton } from "../AddNewValueButton";
import { DropdownValueListItem as DropdownValueListItemComponent } from "./DropdownValueListItem";
import { DropdownValueListItem } from "./types";
import { useDropdownValueList } from "./useDropdownValueList";

interface DropdownValueListProps {
  values: DropdownValueListItem[];
  isReadonly: boolean;
  onValuesChange: (values: DropdownValueListItem[]) => void;
}

export const DropdownValueList: FC<DropdownValueListProps> = ({
  values,
  isReadonly,
  onValuesChange,
}) => {
  const {
    newItem,
    editingItemId,
    handleChangeEditingState,
    handleDismissNewItem,
    handleAddValueClick,
  } = useDropdownValueList();

  const [activeDraggableId, setActiveDraggableId] = useState<string | null>(
    null
  );

  const activeDraggableValue = useMemo(
    () => values.find((value) => value.id === activeDraggableId),
    [activeDraggableId, values]
  );

  const sortableValueIds = useMemo(
    () => values.map((value) => value.id),
    [values]
  );

  const sensors = useSensors(useSensor(MouseSensor));

  const handleAdd = (text: string, color: string) => {
    if (text.trim().length > 0) {
      onValuesChange([...values, { id: getUniqueId(), text, color }]);
    }

    handleDismissNewItem();
  };

  const handleDelete = (id: string) => {
    onValuesChange(values.filter((value) => value.id !== id));
  };

  const handleUpdate = (id: string, text: string, color: string) => {
    if (text.trim().length === 0) {
      handleDelete(id);
      return;
    }

    onValuesChange(
      values.map((value) =>
        value.id === id ? { ...value, text, color } : value
      )
    );
  };

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const activeValue = values.find((value) => value.id === event.active.id);

      if (activeValue && !isReadonly && !activeValue.isReadonly) {
        setActiveDraggableId(activeValue.id);
      }
    },
    [isReadonly, values]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDraggableId(null);

      const { active, over } = event;
      const activeIndex = active.data.current?.index;
      const overIndex = over?.data.current?.index;

      if (
        typeof activeIndex !== "number" ||
        typeof overIndex !== "number" ||
        activeIndex === overIndex ||
        values[overIndex]?.isReadonly
      ) {
        return;
      }

      onValuesChange(reorder(values, activeIndex, overIndex));
    },
    [onValuesChange, values]
  );

  return (
    <div>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sortableValueIds}
          strategy={verticalListSortingStrategy}
          disabled={isReadonly}
        >
          <div>
            {values.map((value, index) => (
              <TaskFieldSidebarListItemLayout.DraggableContainer
                key={value.id}
                id={value.id}
                index={index}
                className="mb-4"
                isDragDisabled={isReadonly || value.isReadonly}
              >
                <DropdownValueListItemComponent
                  text={value.text}
                  color={value.color}
                  isEditing={editingItemId === value.id}
                  isReadonly={isReadonly || value.isReadonly}
                  onChangeEditingState={(isEditing) =>
                    handleChangeEditingState(value.id, isEditing)
                  }
                  onUpdate={(text, color) =>
                    handleUpdate(value.id, text, color)
                  }
                  onDelete={() => handleDelete(value.id)}
                />
              </TaskFieldSidebarListItemLayout.DraggableContainer>
            ))}
          </div>
        </SortableContext>
        {createPortal(
          <DragOverlay style={{ zIndex: 2001 }}>
            {activeDraggableValue && (
              <DropdownValueListItemComponent
                text={activeDraggableValue.text}
                color={activeDraggableValue.color}
                isEditing={false}
                isReadonly
              />
            )}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
      {newItem && (
        <DropdownValueListItemComponent
          text={newItem.text}
          color={newItem.color}
          isEditing={editingItemId === newItem.id}
          isReadonly={isReadonly}
          onChangeEditingState={(isEditing) =>
            handleChangeEditingState(newItem.id, isEditing)
          }
          onUpdate={handleAdd}
          onDelete={handleDismissNewItem}
          className="mb-4"
        />
      )}
      {!isReadonly && <AddNewValueButton onClick={handleAddValueClick} />}
    </div>
  );
};
